/**
 * ContagemTabs - Tab navigation component for Contagem page
 */
import React from 'react';
import { Save, Trash2, Pencil, RefreshCw, Lock } from 'lucide-react';
import { Tooltip } from '@mantine/core';
import PermissionGate from '../PermissionGate';

type TabType = 'agendamento' | 'dados_contagem' | 'itens' | 'cirurgias' | 'fotografias' | 'relatorios';

interface ContagemTabsProps {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  isNewContagem: boolean;
  isReadOnly: boolean;
  isEditMode: boolean;
  setIsEditMode: (mode: boolean) => void;
  onDelete: () => void;
}

const tabs: { id: TabType; label: string }[] = [
  { id: 'agendamento', label: 'Agendamento' },
  { id: 'itens', label: 'Dados da Contagem' },
  { id: 'dados_contagem', label: 'Pós Contagem' },
  { id: 'cirurgias', label: 'Cirurgias a Faturar' },
  { id: 'fotografias', label: 'Fotografias' },
  { id: 'relatorios', label: 'Relatórios' },
];

export const ContagemTabs: React.FC<ContagemTabsProps> = ({
  activeTab,
  setActiveTab,
  isNewContagem,
  isReadOnly,
  isEditMode,
  setIsEditMode,
  onDelete,
}) => {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between px-4">
        {/* Tabs */}
        <div className="flex space-x-1">
          {tabs.map((tab) => {
            // Bloquear outras abas quando for nova contagem
            const isLocked = isNewContagem && tab.id !== 'agendamento';

            const tabButton = (
              <button
                key={tab.id}
                onClick={() => !isLocked && setActiveTab(tab.id)}
                disabled={isLocked}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
                  isLocked
                    ? 'border-transparent text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                    : activeTab === tab.id
                      ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {isLocked && <Lock size={12} className="ml-1" />}
              </button>
            );

            // Envolver com Tooltip se estiver bloqueada
            if (isLocked) {
              return (
                <Tooltip
                  key={tab.id}
                  label="Crie a contagem primeiro para acessar esta aba"
                  position="bottom"
                  withArrow
                >
                  {tabButton}
                </Tooltip>
              );
            }

            return tabButton;
          })}
        </div>

        {/* Action Buttons - Hidden on Pós Contagem tab */}
        {/* N80 (Alana): histórico desta ordem, para não parecer arbitrária.
            1ª correção: o filtro de cliente do header abria a lista do <select>
            sobre os botões e o clique ia parar no Excluir — então o Excluir foi
            para a ESQUERDA do grupo.
            Reteste 24/07: ela pediu explicitamente Editar, Atualizar, Excluir.
            Atendido — o Excluir volta para a direita, mas mantém o afastamento
            (ml-6) e o diálogo de confirmação (useContagemSave.handleDelete), que
            é o que de fato protege contra o clique acidental. */}
        {activeTab !== 'dados_contagem' && (
          <div className="flex items-center space-x-2 py-2 pl-8">
            {!isNewContagem && !isEditMode && (
              <PermissionGate tela="CONTAGENS_LIST" acao="editar">
                <button
                  onClick={() => setIsEditMode(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm font-medium"
                >
                  <Pencil size={16} />
                  <span>Editar</span>
                </button>
              </PermissionGate>
            )}

            <button
              onClick={() => window.location.reload()}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition text-sm font-medium"
            >
              <RefreshCw size={16} />
              <span>Atualizar</span>
            </button>

            {!isNewContagem && (
              <PermissionGate tela="CONTAGENS_LIST" acao="excluir">
                <button
                  onClick={onDelete}
                  className="flex items-center space-x-2 px-4 py-2 ml-6 bg-red-500 text-white rounded-lg hover:bg-red-600 transition text-sm font-medium"
                >
                  <Trash2 size={16} />
                  <span>Excluir</span>
                </button>
              </PermissionGate>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
